import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt, FaUser, FaArrowRight, FaNewspaper } from "react-icons/fa";
import { Link } from "react-router-dom";
import { apiCall } from "../config/postgres";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ParallaxHeroSection from "../components/ParallaxHeroSection";
import { createPlaceholderDataUrl, handleImageError } from "../utils/imageUtils";

const PostCard = ({ post, index, t, i18n }) => {
  const isRTL = i18n.language === "fa";
  const title = isRTL ? post.title_fa : post.title_en;
  const content = isRTL ? post.content_fa : post.content_en;
  const authorName = isRTL ? post.author_name_fa : post.author_name_en;
  
  // Truncate content to 3 lines (approximately 150 characters)
  const truncatedContent = content?.length > 150 
    ? content.substring(0, 150) + "..." 
    : content;

  return (
    <motion.div
      className="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{
        y: -10,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      }}
    >
      {/* Post Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={post.post_image || createPlaceholderDataUrl(400, 200, title)}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
          onError={(e) => handleImageError(e, 400, 200, title)}
        />
      </div>

      <div className="p-6 flex-grow flex flex-col">
        {/* Author Info */}
        <div className="flex items-center mb-4">
          <img
            src={post.author_image || createPlaceholderDataUrl(40, 40, authorName?.charAt(0) || "A")}
            alt={authorName}
            className="w-10 h-10 rounded-full object-cover mr-3 border-2 border-safir-primary"
            onError={(e) => handleImageError(e, 40, 40, authorName?.charAt(0) || "A")}
          />
          <div className="flex-grow">
            <p className="text-sm font-semibold text-safir-dark">
              {authorName || t('posts.defaultAuthor', 'Admin')}
            </p>
            <div className="flex items-center text-xs text-gray-500">
              <FaCalendarAlt className="mr-1" size={12} />
              {new Date(post.created_at).toLocaleDateString(isRTL ? 'fa-IR' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-safir-dark mb-3 line-clamp-2">
          {title}
        </h3>

        {/* Content Preview */}
        <p className="text-gray-600 mb-4 flex-grow line-clamp-3">
          {truncatedContent}
        </p>

        {/* Read More Link */}
        <Link
          to={`/news/${post.id}`}
          className="inline-flex items-center text-safir-primary hover:text-safir-secondary font-semibold transition-colors duration-300"
        >
          {t('posts.readMore')}
          <FaArrowRight className={`ml-2 ${isRTL ? 'rotate-180' : ''}`} />
        </Link>
      </div>
    </motion.div>
  );
};

const PostsPage = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isRTL = i18n.language === "fa";

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);

    const fetchPosts = async () => {
      try {
        const data = await apiCall('/api/posts');
        setPosts(data || []);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching posts:", error);
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <ParallaxHeroSection
        backgroundKey="blog"
        title={t('posts.title')}
        subtitle={t('posts.description')}
      >
        <motion.div
          className="w-24 h-1 bg-safir-primary mx-auto mt-4"
          initial={{ width: 0 }}
          animate={{ width: 96 }}
          transition={{ duration: 0.8 }}
        ></motion.div>
      </ParallaxHeroSection>

      {/* Posts Grid */}
      <div className="container mx-auto px-4 py-8">
        <div className="-mt-16">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-safir-primary mx-auto"></div>
              <p className="mt-4 text-gray-600">{t('common.loading')}</p>
            </div>
          ) : posts.length === 0 ? (
            <motion.div
              className="text-center py-16 bg-white rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <FaNewspaper className="mx-auto mb-4 text-6xl text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                {t('posts.noPosts')}
              </h3>
              <p className="text-gray-500">
                {t('posts.checkBackLater', 'Check back later for updates')}
              </p>
            </motion.div>
          ) : (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center max-w-7xl mx-auto"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {posts.map((post, index) => (
                <PostCard
                  key={post.id}
                  post={post}
                  index={index}
                  t={t}
                  i18n={i18n}
                />
              ))}
            </motion.div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PostsPage;
